import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useApiCall } from '../hooks/useApiCall';

export const CATALOG_TYPES = [
  {
    id: 'lights_living',
    label: 'Lights & Living',
    description: 'Lighting, furniture, appliances, outdoor and home finishing.',
    primary_color: '#b58e69'
  },
  {
    id: 'heavy_machine',
    label: 'Machinery & Equipment',
    description: 'Tools, machines, equipment and heavy duty project supplies.',
    primary_color: '#0155b0'
  }
];

const CATALOG_TYPE_STORAGE_KEY = 'bds_catalog_type';
const CatalogTypeContext = createContext(null);
const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

const buildDefaultCatalogSettings = (types = CATALOG_TYPES) => ({
  kicker: 'Building Depot Suriname',
  title: 'Select your department',
  logo_variant: 'logo_color',
  background_image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1800&q=80',
  options: types.reduce((options, type) => ({
    ...options,
    [type.id]: {
      label: type.label,
      description: type.description,
      primary_color: type.primary_color
    }
  }), {})
});

const DEFAULT_CATALOG_SETTINGS = buildDefaultCatalogSettings();
const isValidCatalogType = (type, types = CATALOG_TYPES) => types.some((item) => item.id === type);
const normalizeHexColor = (color, fallback) => (
  typeof color === 'string' && HEX_COLOR_RE.test(color.trim()) ? color.trim().toLowerCase() : fallback
);

const normalizeCatalogTypes = (departments) => {
  if (!Array.isArray(departments) || departments.length === 0) return CATALOG_TYPES;

  return departments.map((department) => {
    const id = department.slug || department.id;
    const label = department.label || department.name || id;

    return {
      id,
      slug: id,
      label,
      name: department.name || label,
      description: department.description || '',
      primary_color: normalizeHexColor(department.primary_color, '#b58e69'),
      logo_url: department.logo_url || ''
    };
  });
};

const hexToRgb = (hex) => {
  const value = normalizeHexColor(hex, '#b58e69').slice(1);
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16)
  };
};

const rgbToHex = ({ r, g, b }) => `#${[r, g, b].map((part) => (
  Math.max(0, Math.min(255, Math.round(part))).toString(16).padStart(2, '0')
)).join('')}`;

const mixColor = (hex, targetHex, amount) => {
  const color = hexToRgb(hex);
  const target = hexToRgb(targetHex);
  return rgbToHex({
    r: color.r + (target.r - color.r) * amount,
    g: color.g + (target.g - color.g) * amount,
    b: color.b + (target.b - color.b) * amount
  });
};

const getCatalogPalette = (primaryColor = '#b58e69') => {
  const primary = normalizeHexColor(primaryColor, '#b58e69');
  const rgb = hexToRgb(primary);

  return {
    primary,
    primaryHover: mixColor(primary, '#000000', 0.12),
    primaryDark: mixColor(primary, '#000000', 0.22),
    accent: mixColor(primary, '#ffffff', 0.32),
    tint: mixColor(primary, '#ffffff', 0.94),
    soft: mixColor(primary, '#ffffff', 0.88),
    border: mixColor(primary, '#ffffff', 0.82),
    rgb: `${rgb.r}, ${rgb.g}, ${rgb.b}`
  };
};

export const mergeCatalogSelectorSettings = (settings = {}, types = CATALOG_TYPES) => {
  const incomingOptions = settings?.options || {};
  const defaults = buildDefaultCatalogSettings(types);

  return {
    ...defaults,
    ...settings,
    options: types.reduce((options, type) => {
      const current = incomingOptions[type.id] || {};
      const defaultOption = defaults.options[type.id];

      return {
        ...options,
        [type.id]: {
          ...defaultOption,
          ...current,
          label: type.label,
          name: type.name || type.label,
          primary_color: type.primary_color,
          logo_url: type.logo_url || ''
        }
      };
    }, {})
  };
};

const getInitialCatalogType = () => {
  if (typeof window === 'undefined') return null;
  const stored = window.sessionStorage.getItem(CATALOG_TYPE_STORAGE_KEY);
  return isValidCatalogType(stored) ? stored : null;
};

export function CatalogTypeProvider({ children }) {
  const apiCall = useApiCall();
  const [catalogType, setCatalogTypeState] = useState(getInitialCatalogType);
  const [catalogTypes, setCatalogTypes] = useState(CATALOG_TYPES);
  const [catalogSettings, setCatalogSettings] = useState(DEFAULT_CATALOG_SETTINGS);
  const [catalogSettingsLoaded, setCatalogSettingsLoaded] = useState(false);

  useEffect(() => {
    const loader = new AbortController();

    Promise.all([
      apiCall('catalog-departments', 'POST', {}, loader),
      apiCall('pages/catalog-selector', 'POST', {}, loader)
    ])
      .then(([departmentsResponse, settingsResponse]) => {
        const nextTypes = normalizeCatalogTypes(departmentsResponse?.data?.departments);
        setCatalogTypes(nextTypes);

        if (settingsResponse?.success && settingsResponse.data?.settings) {
          setCatalogSettings(mergeCatalogSelectorSettings(settingsResponse.data.settings, nextTypes));
        } else {
          setCatalogSettings(buildDefaultCatalogSettings(nextTypes));
        }
      })
      .finally(() => setCatalogSettingsLoaded(true));

    return () => loader.abort();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setCatalogType = useCallback((type) => {
    if (!isValidCatalogType(type, catalogTypes)) return;
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(CATALOG_TYPE_STORAGE_KEY, type);
    }
    setCatalogTypeState(type);
  }, [catalogTypes]);

  useEffect(() => {
    if (!catalogType || isValidCatalogType(catalogType, catalogTypes)) return;

    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(CATALOG_TYPE_STORAGE_KEY);
    }
    setCatalogTypeState(null);
  }, [catalogType, catalogTypes]);

  const catalogOptions = useMemo(
    () => catalogTypes.map((type) => {
      const option = catalogSettings.options?.[type.id] || {};
      return {
        ...type,
        ...option,
        label: type.label,
        name: type.name || type.label,
        primary_color: type.primary_color,
        logo_url: type.logo_url || ''
      };
    }),
    [catalogSettings, catalogTypes]
  );

  const currentCatalog = useMemo(
    () => catalogOptions.find((item) => item.id === catalogType) || null,
    [catalogOptions, catalogType]
  );

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const palette = getCatalogPalette();
    document.body.style.setProperty('--catalog-primary', palette.primary);
    document.body.style.setProperty('--catalog-primary-hover', palette.primaryHover);
    document.body.style.setProperty('--catalog-primary-dark', palette.primaryDark);
    document.body.style.setProperty('--catalog-accent', palette.accent);
    document.body.style.setProperty('--catalog-tint', palette.tint);
    document.body.style.setProperty('--catalog-soft', palette.soft);
    document.body.style.setProperty('--catalog-border', palette.border);
    document.body.style.setProperty('--catalog-primary-rgb', palette.rgb);

    if (catalogType) {
      document.body.setAttribute('data-catalog-type', catalogType);
    } else {
      document.body.removeAttribute('data-catalog-type');
    }
  }, [catalogType]);

  const value = useMemo(() => ({
    catalogType,
    currentCatalog,
    catalogOptions,
    catalogTypes,
    catalogSettings,
    catalogSettingsLoaded,
    setCatalogType,
    hasCatalogType: Boolean(catalogType)
  }), [catalogType, currentCatalog, catalogOptions, catalogTypes, catalogSettings, catalogSettingsLoaded, setCatalogType]);

  return (
    <CatalogTypeContext.Provider value={value}>
      {children}
    </CatalogTypeContext.Provider>
  );
}

export function useCatalogType() {
  const context = useContext(CatalogTypeContext);
  if (!context) {
    throw new Error('useCatalogType must be used within CatalogTypeProvider');
  }
  return context;
}
